/**
 * TEMP-H-004: Persona Threshold Editor Component
 * 
 * Interactive editor for persona-based QC thresholds with real-time validation.
 * Provides intuitive UI for editing pass/fallback minimums and category settings.
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  User,
  Settings,
  Save,
  X,
  AlertCircle,
  RotateCcw,
  Eye,
  EyeOff,
  Plus,
  Minus,
  HelpCircle
} from 'lucide-react';
import type {
  PersonaThreshold,
  UpdateStageSettingsRequest,
  SettingsFormState,
  CategoryEditorState,
  ValidationResult
} from '@/types/stage-settings';
import type { StageType } from '@/types/execution';

interface PersonaThresholdEditorProps {
  persona: PersonaThreshold;
  executionId: string;
  stage: StageType;
  isEditable: boolean;
  isLoading?: boolean;
  onSave: (updates: UpdateStageSettingsRequest) => Promise<void>;
  onCancel: () => void;
  onValidationChange?: (isValid: boolean) => void;
  className?: string;
}

interface SliderInputProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
  error?: string;
  disabled?: boolean;
  unit?: string;
  description?: string;
}

const SliderInput: React.FC<SliderInputProps> = ({
  label,
  value,
  min,
  max,
  step,
  onChange,
  error,
  disabled = false,
  unit = '%',
  description
}) => {
  const displayValue = unit === '%' ? Math.round(value * 100) : value;
  const displayMin = unit === '%' ? Math.round(min * 100) : min;
  const displayMax = unit === '%' ? Math.round(max * 100) : max;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-white">{label}</label>
          {description && (
            <div className="group relative">
              <HelpCircle className="w-3 h-3 text-gray-400 cursor-help" />
              <div className="absolute left-0 top-6 hidden group-hover:block z-10 w-64 p-2 bg-gray-800 border border-gray-600 rounded-lg text-xs text-gray-300">
                {description}
              </div>
            </div>
          )}
        </div>
        <span className={`text-sm font-mono ${error ? 'text-red-400' : 'text-gray-300'}`}>
          {displayValue}{unit}
        </span>
      </div>
      
      <div className="relative">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          disabled={disabled}
          className={`w-full h-2 rounded-lg appearance-none cursor-pointer ${
            error
              ? 'bg-red-900/20 slider-thumb-red'
              : 'bg-gray-700 slider-thumb-purple'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        />
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>{displayMin}{unit}</span>
          <span>{displayMax}{unit}</span>
        </div>
      </div>
      
      {error && (
        <div className="flex items-center gap-1 text-xs text-red-400">
          <AlertCircle className="w-3 h-3" />
          {error}
        </div>
      )}
    </div>
  );
};

export function PersonaThresholdEditor({
  persona,
  executionId: _executionId,
  stage: _stage,
  isEditable,
  isLoading = false,
  onSave,
  onCancel,
  onValidationChange,
  className = ''
}: PersonaThresholdEditorProps) {
  const [formState, setFormState] = useState<SettingsFormState>({
    isDirty: false,
    isValid: true,
    isSubmitting: false,
    errors: {},
    originalValues: persona,
    currentValues: { ...persona }
  });

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [categoryState, setCategoryState] = useState<CategoryEditorState>({
    categories: {},
    totalWeight: 0,
    isWeightedSumValid: true
  });

  // Initialize category state
  useEffect(() => {
    const categories: CategoryEditorState['categories'] = {};
    let totalWeight = 0;

    Object.entries(persona.categoryThresholds || {}).forEach(([key, threshold]) => {
      categories[key] = {
        threshold,
        weight: persona.categoryWeights?.[key] || 0,
        isValid: threshold >= 0 && threshold <= 1,
        error: undefined
      };
      totalWeight += persona.categoryWeights?.[key] || 0;
    });

    setCategoryState({
      categories,
      totalWeight,
      isWeightedSumValid: Math.abs(totalWeight - 1.0) < 0.001
    });
  }, [persona]);

  // Validation logic
  const validation = useMemo((): ValidationResult => {
    const errors: Record<string, string> = {};
    const current = formState.currentValues;

    // Basic threshold validation
    if (current.passMin !== undefined && (current.passMin < 0.1 || current.passMin > 1.0)) {
      errors.passMin = 'Pass minimum must be between 10% and 100%';
    }
    
    if (current.fallbackMin !== undefined && (current.fallbackMin < 0.05 || current.fallbackMin > 0.95)) {
      errors.fallbackMin = 'Fallback minimum must be between 5% and 95%';
    }
    
    // Cross-validation
    if (current.passMin && current.fallbackMin && current.passMin <= current.fallbackMin) {
      errors.passMin = 'Pass minimum must be greater than fallback minimum';
    }
    
    // Retry limit validation
    if (current.retryLimit !== undefined && (current.retryLimit < 1 || current.retryLimit > 10)) {
      errors.retryLimit = 'Retry limit must be between 1 and 10';
    }

    // Category validation
    Object.entries(categoryState.categories).forEach(([key, category]) => {
      if (!category.isValid) {
        errors[`category_${key}`] = category.error || 'Invalid category threshold';
      }
    });

    if (!categoryState.isWeightedSumValid) {
      errors.categoryWeights = `Category weights must sum to 1.0 (currently ${categoryState.totalWeight.toFixed(3)})`;
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }, [formState.currentValues, categoryState]);

  // Update form state when validation changes
  useEffect(() => {
    setFormState(prev => ({
      ...prev,
      isValid: validation.isValid,
      errors: validation.errors
    }));
    
    onValidationChange?.(validation.isValid);
  }, [validation, onValidationChange]);

  // Handle field updates
  const updateField = useCallback((field: keyof PersonaThreshold, value: any) => {
    setFormState(prev => ({
      ...prev,
      isDirty: true,
      currentValues: {
        ...prev.currentValues,
        [field]: value
      }
    }));
  }, []);

  // Handle category threshold updates
  const updateCategoryThreshold = useCallback((categoryKey: string, threshold: number) => {
    setCategoryState(prev => {
      const isValid = threshold >= 0 && threshold <= 1;
      const newCategories = {
        ...prev.categories,
        [categoryKey]: {
          ...prev.categories[categoryKey],
          threshold,
          isValid,
          error: isValid ? undefined : 'Threshold must be between 0% and 100%'
        }
      };

      return {
        ...prev,
        categories: newCategories
      };
    });

    // Update form state
    const newCategoryThresholds = {
      ...formState.currentValues.categoryThresholds,
      [categoryKey]: threshold
    };
    updateField('categoryThresholds', newCategoryThresholds);
  }, [formState.currentValues.categoryThresholds, updateField]);

  // Handle category weight updates
  const updateCategoryWeight = useCallback((categoryKey: string, weight: number) => {
    setCategoryState(prev => {
      const newCategories = {
        ...prev.categories,
        [categoryKey]: {
          ...prev.categories[categoryKey],
          weight
        }
      };

      const newTotalWeight = Object.values(newCategories).reduce((sum, cat) => sum + (cat.weight || 0), 0);
      const isWeightedSumValid = Math.abs(newTotalWeight - 1.0) < 0.001;

      return {
        categories: newCategories,
        totalWeight: newTotalWeight,
        isWeightedSumValid
      };
    });

    // Update form state
    const newCategoryWeights = {
      ...formState.currentValues.categoryWeights,
      [categoryKey]: weight
    };
    updateField('categoryWeights', newCategoryWeights);
  }, [formState.currentValues.categoryWeights, updateField]);

  // Add new category
  const addCategory = useCallback(() => {
    const newKey = `category_${Date.now()}`;
    setCategoryState(prev => ({
      ...prev,
      categories: {
        ...prev.categories,
        [newKey]: {
          threshold: 0.7,
          weight: 0,
          isValid: true
        }
      }
    }));
  }, []);

  // Remove category
  const removeCategory = useCallback((categoryKey: string) => {
    setCategoryState(prev => {
      const newCategories = { ...prev.categories };
      delete newCategories[categoryKey];
      
      const newTotalWeight = Object.values(newCategories).reduce((sum, cat) => sum + (cat.weight || 0), 0);
      
      return {
        categories: newCategories,
        totalWeight: newTotalWeight,
        isWeightedSumValid: Math.abs(newTotalWeight - 1.0) < 0.001
      };
    });
  }, []);

  // Handle save
  const handleSave = useCallback(async () => {
    if (!validation.isValid) return;

    setFormState(prev => ({ ...prev, isSubmitting: true }));

    try {
      const updates: UpdateStageSettingsRequest = {};

      // Only include changed values
      if (formState.currentValues.passMin !== persona.passMin) {
        updates.passMin = formState.currentValues.passMin;
      }
      if (formState.currentValues.fallbackMin !== persona.fallbackMin) {
        updates.fallbackMin = formState.currentValues.fallbackMin;
      }
      if (formState.currentValues.retryLimit !== persona.retryLimit) {
        updates.retryLimit = formState.currentValues.retryLimit;
      }

      // Category thresholds
      const hasThresholdChanges = Object.entries(categoryState.categories).some(([key, category]) => 
        category.threshold !== persona.categoryThresholds?.[key]
      );
      if (hasThresholdChanges) {
        updates.categoryThresholds = Object.fromEntries(
          Object.entries(categoryState.categories).map(([key, category]) => [key, category.threshold])
        );
      }

      // Category weights  
      const hasWeightChanges = Object.entries(categoryState.categories).some(([key, category]) =>
        category.weight !== persona.categoryWeights?.[key]
      );
      if (hasWeightChanges) {
        updates.categoryWeights = Object.fromEntries(
          Object.entries(categoryState.categories).map(([key, category]) => [key, category.weight || 0])
        );
      }

      await onSave(updates);
      
      // Reset form state on success
      setFormState(prev => ({
        ...prev,
        isDirty: false,
        isSubmitting: false,
        originalValues: formState.currentValues
      }));
    } catch (error) {
      setFormState(prev => ({ ...prev, isSubmitting: false }));
      // Error handling done by parent component
    }
  }, [validation.isValid, formState.currentValues, persona, categoryState, onSave]);

  // Handle reset
  const handleReset = useCallback(() => {
    setFormState({
      isDirty: false,
      isValid: true,
      isSubmitting: false,
      errors: {},
      originalValues: persona,
      currentValues: { ...persona }
    });
  }, [persona]);

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
            <User className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">{persona.personaName}</h3>
            <p className="text-sm text-gray-400">{persona.personaRole}</p>
          </div>
        </div>
        
        {formState.isDirty && (
          <div className="flex items-center gap-2 text-xs text-amber-400">
            <AlertCircle className="w-3 h-3" />
            Unsaved changes
          </div>
        )}
      </div>

      {/* Basic Thresholds */}
      <div className="space-y-4">
        <h4 className="text-sm font-medium text-gray-300 flex items-center gap-2">
          <Settings className="w-4 h-4" />
          Quality Thresholds
        </h4>
        
        <div className="grid gap-4">
          <SliderInput
            label="Pass Minimum"
            value={formState.currentValues.passMin || persona.passMin}
            min={0.1}
            max={1.0}
            step={0.01}
            onChange={(value) => updateField('passMin', value)}
            error={formState.errors.passMin}
            disabled={!isEditable || isLoading}
            description="Minimum score required for automatic pass"
          />
          
          <SliderInput
            label="Fallback Minimum" 
            value={formState.currentValues.fallbackMin || persona.fallbackMin}
            min={0.05}
            max={0.95}
            step={0.01}
            onChange={(value) => updateField('fallbackMin', value)}
            error={formState.errors.fallbackMin}
            disabled={!isEditable || isLoading}
            description="Minimum score to avoid automatic failure"
          />
          
          <SliderInput
            label="Retry Limit"
            value={formState.currentValues.retryLimit || persona.retryLimit}
            min={1}
            max={10}
            step={1}
            onChange={(value) => updateField('retryLimit', value)}
            error={formState.errors.retryLimit}
            disabled={!isEditable || isLoading}
            unit=""
            description="Maximum number of retry attempts"
          />
        </div>
      </div>

      {/* Advanced Settings Toggle */}
      <div>
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
        >
          {showAdvanced ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          {showAdvanced ? 'Hide' : 'Show'} Advanced Settings
        </button>
      </div>

      {/* Advanced Category Settings */}
      {showAdvanced && (
        <div className="space-y-4 border-t border-gray-700 pt-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-gray-300">Category Thresholds</h4>
            {isEditable && (
              <button
                type="button"
                onClick={addCategory}
                disabled={isLoading}
                className="flex items-center gap-1 text-xs text-purple-400 hover:text-purple-300 disabled:opacity-50"
              >
                <Plus className="w-3 h-3" />
                Add Category
              </button>
            )}
          </div>

          <div className="space-y-3">
            {Object.entries(categoryState.categories).map(([categoryKey, category]) => (
              <div key={categoryKey} className="bg-gray-800/50 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-white capitalize">
                    {categoryKey.replace(/_/g, ' ')}
                  </span>
                  {isEditable && Object.keys(categoryState.categories).length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeCategory(categoryKey)}
                      disabled={isLoading}
                      className="text-red-400 hover:text-red-300 disabled:opacity-50"
                    >
                      <Minus className="w-3 h-3" />
                    </button>
                  )}
                </div>
                
                <SliderInput
                  label="Threshold"
                  value={category.threshold}
                  min={0}
                  max={1}
                  step={0.01}
                  onChange={(value) => updateCategoryThreshold(categoryKey, value)}
                  error={category.error}
                  disabled={!isEditable || isLoading}
                />
                
                <SliderInput
                  label="Weight"
                  value={category.weight || 0}
                  min={0}
                  max={1}
                  step={0.01}
                  onChange={(value) => updateCategoryWeight(categoryKey, value)}
                  disabled={!isEditable || isLoading}
                  unit=""
                  description="Weight in overall score calculation"
                />
              </div>
            ))}
          </div>

          {formState.errors.categoryWeights && (
            <div className="flex items-center gap-2 text-xs text-red-400">
              <AlertCircle className="w-3 h-3" />
              {formState.errors.categoryWeights}
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      {isEditable && (
        <div className="flex items-center justify-between pt-4 border-t border-gray-700">
          <div className="flex items-center gap-2">
            {formState.isDirty && (
              <button
                type="button"
                onClick={handleReset}
                disabled={isLoading}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-400 hover:text-white transition-colors disabled:opacity-50"
              >
                <RotateCcw className="w-3 h-3" />
                Reset
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading || formState.isSubmitting}
              className="flex items-center gap-1.5 px-4 py-2 text-sm border border-gray-600 hover:border-gray-500 text-gray-300 hover:text-white rounded-lg transition-colors disabled:opacity-50"
            >
              <X className="w-3 h-3" />
              Cancel
            </button>

            <button
              type="button"
              onClick={handleSave}
              disabled={!formState.isDirty || !formState.isValid || isLoading || formState.isSubmitting}
              className="flex items-center gap-1.5 px-4 py-2 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors disabled:opacity-50"
            >
              {formState.isSubmitting ? (
                <div className="w-3 h-3 border border-white/20 border-t-white rounded-full animate-spin" />
              ) : (
                <Save className="w-3 h-3" />
              )}
              Save Changes
            </button>
          </div>
        </div>
      )}

      {/* Validation Summary */}
      {!validation.isValid && Object.keys(validation.errors).length > 0 && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-red-300">Validation Errors</p>
              <ul className="text-xs text-red-400 space-y-1">
                {Object.entries(validation.errors).map(([field, error]) => (
                  <li key={field}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}